import React, { useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Switch from "@material-ui/core/Switch";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import Autocomplete from "@material-ui/lab/Autocomplete";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const SEARCH_DEBOUNCE_MS = 400;
const LOAD_MORE_THRESHOLD_PX = 40;

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    section: {
      background: theme.palette.background.default,
      borderRadius: 8,
      padding: theme.spacing(1.5),
      border: `1px solid ${theme.palette.divider}`,
      marginTop: theme.spacing(1),
    },
    switchRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: theme.spacing(1, 2),
      backgroundColor: dark ? "rgba(255,255,255,0.04)" : theme.palette.background.paper,
      borderRadius: 8,
      border: `1px solid ${dark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.23)"}`,
    },
    title: {
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
    help: {
      color: theme.palette.text.secondary,
      fontSize: "0.8rem",
      marginTop: theme.spacing(0.75),
    },
    selector: {
      marginTop: theme.spacing(1.5),
      "& .MuiOutlinedInput-root": { borderRadius: 8 },
      // O tema fixa height: 35 em campos dense; com várias linhas de chips eles vazavam da borda.
      "& .MuiAutocomplete-inputRoot.MuiOutlinedInput-root": {
        height: "auto",
        minHeight: 40,
        maxHeight: 200,
        overflowY: "auto",
        flexWrap: "wrap",
        gap: 2,
      },
    },
    chip: {
      borderRadius: 8,
      backgroundColor: dark ? theme.palette.action.selected : theme.palette.background.contrast,
      border: `1px solid ${theme.palette.divider}`,
      color: theme.palette.text.primary,
      margin: "2px 3px !important",
      height: "24px !important",
      maxWidth: "100%",
      "& .MuiChip-label": {
        paddingLeft: 8,
        paddingRight: 6,
        fontWeight: 500,
        fontSize: "0.8rem",
        lineHeight: "24px",
      },
      "& .MuiChip-deleteIcon": {
        color: theme.palette.error.main,
        width: 18,
        height: 18,
        marginRight: 4,
        marginLeft: -2,
        "&:hover": { color: theme.palette.error.dark },
      },
    },
  };
});

const GroupAccessField = ({
  targetUserId,
  whatsappId,
  seeAllGroups,
  onSeeAllGroupsChange,
  selected,
  onSelectedChange,
  onSelectionLoaded,
}) => {
  const classes = useStyles();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [options, setOptions] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hiddenCount, setHiddenCount] = useState(0);

  const scopeKey = `${targetUserId || "new"}:${whatsappId || ""}`;
  const loadedScopeRef = useRef(null);
  const onSelectionLoadedRef = useRef(onSelectionLoaded);
  onSelectionLoadedRef.current = onSelectionLoaded;

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, scopeKey]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api
      .get(`/users/${targetUserId || "new"}/group-access-options`, {
        params: {
          whatsappId: whatsappId || "",
          search: debouncedSearch || undefined,
          pageNumber: page,
        },
      })
      .then(({ data }) => {
        if (!active) return;
        const groups = Array.isArray(data?.groups) ? data.groups : [];
        setOptions((prev) => (page === 1 ? groups : [...prev, ...groups]));
        setHasMore(Boolean(data?.hasMore));
        const savedSelection = Array.isArray(data?.selected) ? data.selected : [];
        setHiddenCount(savedSelection.filter((g) => !g.selectable).length);
        if (loadedScopeRef.current !== scopeKey) {
          loadedScopeRef.current = scopeKey;
          onSelectionLoadedRef.current?.(savedSelection.filter((g) => g.selectable));
        }
      })
      .catch((err) => {
        if (active) toastError(err);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [targetUserId, whatsappId, debouncedSearch, page, scopeKey]);

  const handleListboxScroll = (event) => {
    const el = event.currentTarget;
    if (!hasMore || loading) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - LOAD_MORE_THRESHOLD_PX) {
      setPage((current) => current + 1);
    }
  };

  return (
    <div className={classes.section}>
      <div className={classes.switchRow}>
        <Typography variant="body2" className={classes.title}>
          {i18n.t("userModal.form.seeAllGroups")}
        </Typography>
        <Switch
          color="primary"
          checked={seeAllGroups === true}
          onChange={(event) => onSeeAllGroupsChange(event.target.checked)}
          inputProps={{ "aria-label": i18n.t("userModal.form.seeAllGroups") }}
        />
      </div>
      <Typography className={classes.help}>{i18n.t("userModal.form.seeAllGroupsHelp")}</Typography>

      {seeAllGroups !== true && (
        <>
          <Autocomplete
            multiple
            className={classes.selector}
            options={options}
            value={selected}
            loading={loading}
            filterOptions={(x) => x}
            getOptionLabel={(option) => option?.name || ""}
            getOptionSelected={(option, value) => option.id === value.id}
            onChange={(_event, value) => onSelectedChange(value)}
            onInputChange={(_event, value, reason) => {
              if (reason === "input") setSearch(value);
            }}
            ListboxProps={{ onScroll: handleListboxScroll }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={option.id}
                  size="small"
                  className={classes.chip}
                  label={option.name}
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                label={i18n.t("userModal.form.groupAccess")}
                placeholder={selected.length === 0 ? i18n.t("userModal.form.groupAccessPlaceholder") : ""}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          {selected.length === 0 && (
            <Typography className={classes.help}>{i18n.t("userModal.form.groupAccessEmpty")}</Typography>
          )}
          {hiddenCount > 0 && (
            <Typography className={classes.help}>
              {i18n.t("userModal.form.groupAccessHidden", { count: hiddenCount })}
            </Typography>
          )}
        </>
      )}
    </div>
  );
};

export default GroupAccessField;
